/* eslint-disable */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { formatMixedTimestampDisplayDate } from "./dateUtils";
// NOTE: this logic moved to a shared module so the exported sheet resolves
// WFH/WFO exactly the way this table does (it used to use its own rules and
// produced blank cells for hours that are visible here).
import {
  getOrphanWorkLocationDates,
  getWorkLocationHours,
  getWorkLocationHoursForDateKey,
  resolveUserWorkLocations,
} from "./workLocationUtils";

type Detail = {
  date?: any;
  id: number;
  day: number;
  task: string;
  hour: number;
  comment: string;
};

type ProjectData = {
  id: number;
  projectId: number;
  detail: Detail[];
  comment: string;
  status: string;
  totalHour: number;
};

type UserData = {
  id: number;
  userName: string;
  email: string;
  year: number;
  month: string;
  status: string;
  projectIds: string;
  submitDate: string;
  totalHour: number;
  comment: string;
  data: ProjectData[];
};

type DataDisplayProps = {
  data: UserData[];
  currentUserId?: string;
  userName?: any;
  projectName?: any;
  workLocations?: Record<string, any>;
  workLocationSummary?: Record<string, any> | null;
  onApprove?: (user: any) => void;
};
const DataDisplay = ({
  data,
  userName,
  projectName,
  workLocations,
  workLocationSummary,
  onApprove,
  currentUserId
}: DataDisplayProps) => {
  const [expandedUserIds, setExpandedUserIds] = useState<number[]>([]);

  const toggleExpand = (userId: number) => {
    setExpandedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };
  const { t } = useTranslation();
  return (
    <div className="mt-0">
      <header className="bg-gray-800 dark:bg-gray-900 text-white p-4 shadow-lg">
        <h1 className="text-3xl font-bold">User Data</h1>
      </header>
      <div className="w-full p-4">
        {data.map((user) => {
        console.log(">>>>>>>>user",user)
        console.log("currentuser======",currentUserId)
          // Backward compatibility (map keyed by user vs. legacy flat single-user
          // map) is handled inside resolveUserWorkLocations.
          const userWorkLocations = resolveUserWorkLocations(workLocations, user);

          const isNestedSummary = workLocationSummary && !('wfhHours' in workLocationSummary || 'wfoHours' in workLocationSummary);
          const userSummary = isNestedSummary
            ? (workLocationSummary?.[user.email] || workLocationSummary?.[user.id] || {})
            : (workLocationSummary || {});

          // 1. Determine if the user has timesheet data or orphaned work locations
          const orphanDates = getOrphanWorkLocationDates(user, userWorkLocations);
          const hasTimesheetData = user.data && user.data.length > 0;
          const hasOrphanWL = orphanDates.length > 0;

          // 2. If neither exists, do not render the card
          if (!hasTimesheetData && !hasOrphanWL) return null;

          const resolvedUserName =
            userName?.(user.email) || user.userName || user.email || "-";

          return (
            <div
              key={user.id}
              className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-6 border-l-4 border-blue-500 dark:border-blue-400 transition-transform"
            >
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-semibold text-gray-700 dark:text-gray-200 mb-2">
                    {resolvedUserName}
                  </h2>
                  <p className="text-green-600 dark:text-green-400">
                    Total Hours : <strong> {user.totalHour} </strong>
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    WFH Hours : <strong>{userSummary?.wfhHours ?? "-"}</strong>
                  </p>
                  <p className="text-gray-600 dark:text-gray-300">
                    WFO Hours : <strong>{userSummary?.wfoHours ?? "-"}</strong>
                  </p>
                </div>

                <div className="flex gap-2">
                  {onApprove && String(user.email) !== currentUserId && (
                    <button
                      className="px-4 py-2 text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                      onClick={() => {
                        const projects = user.data
                          .filter((project) => project.totalHour > 0)
                          .map((project) => ({
                            projectId: project.projectId,
                            name: projectName(project.projectId),
                          }));
                        onApprove({
                          ...user,
                          projects,
                        });
                      }}
                    >
                      Approve
                    </button>
                  )}
                  <button
                    className="text-blue-500 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                    onClick={() => toggleExpand(user.id)}
                  >
                    {expandedUserIds.includes(user.id) ? "Minimize" : "Expand"}
                  </button>
                </div>
              </div>

              {expandedUserIds.includes(user.id) && (
                <>
                  <div className="flex flex-wrap mb-4">
                    <div className="w-full md:w-1/2 lg:w-1/4 p-2">
                      <p className="text-gray-600 dark:text-gray-300">
                        <strong>Year:</strong> {user.year}
                      </p>
                    </div>
                    <div className="w-full md:w-1/2 lg:w-1/4 p-2">
                      <p className="text-gray-600 dark:text-gray-300">
                        <strong>Month:</strong> {user.month}
                      </p>
                    </div>
                    <div className="w-full md:w-1/2 lg:w-1/4 p-2">
                      <p
                        className={`text-gray-600 dark:text-gray-300 ${user.status === "InProgress"
                            ? "text-yellow-500 dark:text-yellow-400"
                            : "text-green-500 dark:text-green-400"
                          }`}
                      >
                        <strong>Status:</strong> {user.status}
                      </p>
                    </div>
                    {/* <div className="w-full md:w-1/2 lg:w-1/4 p-2">
                      <p className="text-gray-600 dark:text-gray-300">
                        <strong>Total Hours:</strong> {user.totalHour}
                      </p>
                    </div> */}
                    <div className="w-full p-2">
                      <p className="text-gray-600 dark:text-gray-300 mb-4">
                        <strong>Comment:</strong> {user.comment}
                      </p>
                    </div>
                  </div>

                  {/* Timesheet Data Rendering */}
                  {user.data
                    .filter((project) => project.totalHour > 0)
                    .map(
                      (project) =>
                        project.detail.length > 0 && (
                          <div
                            key={project.id}
                            className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow-inner mb-4 transition-transform transform"
                          >
                            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                              Project Name: {projectName(project.projectId)}
                            </h3>
                            {/* <p className="text-gray-600 dark:text-gray-300">
                              <strong>Status:</strong> {project.status}
                            </p> */}
                            <p className="text-gray-600 dark:text-gray-300">
                              <strong>{t("Total Hours:")}</strong>{" "}
                              {project.totalHour}
                            </p>
                            <table className="min-w-full mt-2">
                              <thead>
                                <tr>
                                  <th className="py-2 px-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200">
                                    Date
                                  </th>
                                  <th className="py-2 min-w-96 px-4 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200">
                                    Task
                                  </th>
                                  <th className="py-2 px-4 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200">
                                    WFH
                                  </th>
                                  <th className="py-2 px-4 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200">
                                    WFO
                                  </th>
                                  <th className="py-2 px-4 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200">
                                    Hours
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {[...project.detail]
                                  .sort((a, b) => a.day - b.day)
                                  .filter((detail) => detail.hour > 0)
                                  .map((detail) => {
                                    const locationHours = getWorkLocationHours(
                                      userWorkLocations,
                                      detail.day,
                                      detail.date
                                    );

                                    return (
                                      <tr
                                        key={detail.id}
                                        className="border-b last:border-0 dark:border-gray-600"
                                      >
                                        <td className="border dark:border-gray-600 w-32 px-4 py-2 text-gray-600 dark:text-gray-300">
                                          {detail.date
                                            ? formatMixedTimestampDisplayDate(detail.date) || detail.day
                                            : detail.day}
                                        </td>
                                        <td className="border dark:border-gray-600 min-w-96 px-4 py-2 text-gray-600 dark:text-gray-300">
                                          {detail.task}
                                        </td>
                                        <td className="dark:border-gray-600 dark:text-gray-300 border px-4 py-2">
                                          {locationHours.wfh}
                                        </td>
                                        <td className="dark:border-gray-600 dark:text-gray-300 border px-4 py-2">
                                          {locationHours.wfo}
                                        </td>
                                        <td className="border dark:border-gray-600 px-4 py-2 text-gray-600 dark:text-gray-300">
                                          {detail.hour}
                                        </td>
                                      </tr>
                                    );
                                  })}
                              </tbody>
                            </table>
                          </div>
                        )
                    )}

                  {/* Orphan work-location dates rendering */}
                  {hasOrphanWL && (
                    <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg shadow-inner mb-4">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-2">
                        Work Location (No Timesheet Entry)
                      </h3>
                      <table className="min-w-full mt-2">
                        <thead>
                          <tr>
                            <th className="py-2 px-2 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-left">Date</th>
                            <th className="py-2 min-w-96 px-4 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-left">Task</th>
                            {/* Added text-center here */}
                            <th className="py-2 px-4 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-center">WFH</th>
                            <th className="py-2 px-4 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-center">WFO</th>
                            <th className="py-2 px-4 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-center">Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orphanDates.map((dateKey) => {
                            const orphanHours = getWorkLocationHoursForDateKey(
                              userWorkLocations,
                              dateKey
                            );
                            const displayDate = new Date(dateKey + "T00:00:00")
                              .toLocaleDateString("en-GB", {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              })
                              .replace(/ /g, "-");

                            return (
                              <tr key={dateKey} className="border-b last:border-0 dark:border-gray-600">
                                <td className="border dark:border-gray-600 w-32 px-4 py-2 text-gray-600 dark:text-gray-300">
                                  {displayDate}
                                </td>
                                <td className="border dark:border-gray-600 min-w-96 px-4 py-2 text-gray-600 dark:text-gray-300">-</td>
                                {/* Added text-center here */}
                                <td className="dark:border-gray-600 dark:text-gray-300 border px-4 py-2 text-center">
                                  {orphanHours.wfh}
                                </td>
                                <td className="dark:border-gray-600 dark:text-gray-300 border px-4 py-2 text-center">
                                  {orphanHours.wfo}
                                </td>
                                <td className="border dark:border-gray-600 px-4 py-2 text-gray-600 dark:text-gray-300 text-center">-</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DataDisplay;